import { Injectable } from "@angular/core";
import { Subject, Subscription } from "rxjs";

// BFX
import { GoldenLayoutService } from "../../golden-layout/golden-layout-service";
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// order-entry services
import { OEUtilityService } from "./basic/oe-utility.service";
import { OEModelGetterService } from "./model/oe-model-getter.service";
import { OEModelSetterService } from "./model/oe-model-setter.service";
import { OEStrategyLayoutService } from "./oe-strategy-layout.service";
// order-entry models
import { OECommandEventModel } from "../data/models/oe-command-event.model";
import { OEInputModel } from "../data/models/oe-input.model";
import {
  OEInputLockStateItemModel,
  OELockingProfileModel,
  OELockingProfileTransportModel,
} from "../data/models/oe-locking-profile.model";
// order-entry enums
import { IActionCallout } from "app/bfx-element-library/models/CalloutModel";
import { OENDisplayState } from "app/order-entry/data/enums/oe-display-state.enum";
import { OENOperationMode } from "app/order-entry/data/enums/oe-operation-mode.enum";
import { OENCommand } from "../data/enums/oe-command.enum";
import { OENExtendedLoggingType } from "../data/enums/oe-extended-logging-type.enum";
import { OENExternalCommand } from "../data/enums/oe-external-command.enum";
import { OENInputControl } from "../data/enums/oe-input-control.enum";
import { OENLockedState } from "../data/enums/oe-locked-state.enum";
import { OENLockingProfileType } from "../data/enums/oe-locking-profile-type.enum";
import { OENPanel } from "../data/enums/oe-panel.enum";
import {
  INPUT_TO_PANEL_MAPPING,
  MKT_ORDER_LOCK_MAPPING,
} from "../data/mappings/oe-lock-mapping.const";
import { OEDLoadingDataModel } from "../data/models/oe-loading-data.model";
import { OEPanelGammaLeg } from "../data/models/oe-panel-gamma-leg.model";
import { OELoggerService } from "./basic/oe-logger.service";
///

const OE_DISPLAY_STATE_COMMANDS = new Set([
  OENCommand.STATE_ORDER_ENTRY,
  OENCommand.STATE_ORDER_IN_MODIFICATION,
  OENCommand.STATE_ORDER_AMEND_MARKUP,
  OENCommand.STATE_ORDER_UNLOCKED,
  OENCommand.STATE_ORDER_LOST_HIDDEN,
  OENCommand.STATE_ORDER_PROCESSING,
  OENCommand.STATE_ORDER_SUCCESS,
  OENCommand.STATE_MONITOR_EXECUTION,
  OENCommand.STATE_ORDER_REJECTED,
  OENCommand.STATE_ORDER_REJECTED_HCO_ERROR,
  OENCommand.STATE_ORDER_INT_ERROR,
  OENCommand.STATE_ORDER_ENTRY_TOLERANCE_WARN,
  OENCommand.STATE_ORDER_ENTRY_TOLERANCE_ERROR,
  OENCommand.STATE_ORDER_APPROVAL_ALLOWED,
  OENCommand.STATE_ORDER_APPROVAL_INTERNAL,
  OENCommand.STATE_ORDER_APPROVAL_EXTERNAL,
  OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE,
  OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE,
  OENCommand.STATE_ORDER_EXPIRY_PENDING,
  OENCommand.STATE_ORDER_ACCEPTANCE_ALLOWED,
  OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL,
  OENCommand.STATE_ORDER_MODIFICATION_EXTERNAL,
  OENCommand.STATE_ORDER_ACCEPTANCE_CALL,
  OENCommand.STATE_ORDER_ENTRY_NEGATIVE_MARKUP,
  OENCommand.BM_ZERO_AMOUNT_WARN,
]);

@Injectable()
export class OECoordinatorService {
  /**********************************************************************/
  /* DATA */

  /**********************************************************************/
  /* SUBSCRIPTIONs */
  private layoutSubscription: Subscription;
  /**********************************************************************/
  /* OBSERVABLE */
  // Subjects
  // panels
  private entryPanelControlSource = new Subject<OECommandEventModel>();
  private actionsPanelControlSource = new Subject<OECommandEventModel>();
  private notificationsPanelControlSource = new Subject<OECommandEventModel>();
  private debugPanelControlSource = new Subject<OECommandEventModel>();
  private strategyPanelControlSource = new Subject<OECommandEventModel>();
  private algoPanelControlSource = new Subject<OECommandEventModel>();
  // screen
  private orderEntryScreenControlSource = new Subject<OECommandEventModel>();

  // Observable string streams
  // panels
  public entryPanelCmd$ = this.entryPanelControlSource.asObservable();
  public actionsPanelCmd$ = this.actionsPanelControlSource.asObservable();
  public notificationsPanelCmd$ = this.notificationsPanelControlSource.asObservable();
  public strategyPanelCmd$ = this.strategyPanelControlSource.asObservable();
  public debugPanelCmd$ = this.debugPanelControlSource.asObservable();
  public algoPanelCmd$ = this.algoPanelControlSource.asObservable();
  // screen
  public orderEntryScrCmd$ = this.orderEntryScreenControlSource.asObservable();

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    protected oeLogger: OELoggerService,
    private _layoutService: GoldenLayoutService,
    private getter: OEModelGetterService,
    private setter: OEModelSetterService,
    private strategyLayout: OEStrategyLayoutService,
    private utility: OEUtilityService,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
    this.initializeSubscriptions();
  }

  /**********************************************************************/
  /* INITIALIZATION */
  private initializeSubscriptions(): void {
    this.subscribeToLayout();
  }

  private subscribeToLayout(): void {
    this.layoutSubscription = this._layoutService.orderEntryCmd$.subscribe(
      (event: { cmd: OENExternalCommand; data: any }) => {
        if (
          event.cmd === OENExternalCommand.CANCEL_LAUNCHED &&
          event.data != null &&
          event.data.length > 0
        ) {
          event.data.forEach((orderId: string) => {
            let refId = this.getter.findRefIdForOrderId(orderId);
            if (refId != null && this.getter.isOperationModeModifyInfo(refId)) {
              logger.out.debug(
                `DEBUG: Order refId: '${refId}' opened in MODIFY_INFO, closing window!`,
              );
              //this.sendErrorNotification(BfxOrderEntryNotificationType.ERROR_MODIFY_INFO_ACTIONED, null, orderId);
              this.cmdCloseOrderEntryScreen(refId);
            }
          });
        }
      },
    );
  }
  /**********************************************************************/
  /* Service message commands */

  /* CMD - New model same interface */
  public cmdNewModel(refId: string, newRefId: string): void {
    try {
      this.setter.setActivelyDisplayed(newRefId);
      let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
      let newRootRefId = this.getter.getRootIdForNodeWithRefId(newRefId);
      this.orderEntryScreenControlSource.next(
        new OECommandEventModel(rootRefId, OENCommand.NEW_MODEL, newRootRefId),
      );
    } catch (ex) {
      logger.out.error("ERROR: _oeCoordinator.cmdNewModel(): exception:", ex);
    }
  }

  /* CMD -  Load Order Entry Defaults  */
  /**
   * The loading of order entry defaults must be handled through the screen since _oeActions is not refId specific
   * currently this function is not in use
   */
  public cmdLoadDefaults(refId: string): void {
    if (this.getter.has(refId)) {
      this.orderEntryScreenControlSource.next(
        new OECommandEventModel(refId, OENCommand.LOAD_DEFAULTS),
      );
    }
  }

  public cmdInvalidInput(refId: string, errors: Array<OEInputModel>): void {
    if (errors != null && Array.isArray(errors)) {
      let scrollInViewIssued = false;

      for (let error of errors) {
        if (error instanceof OEInputModel && error.panel === OENPanel.PANEL_ALGO) {
          this.algoPanelControlSource.next(
            new OECommandEventModel(refId, OENCommand.INVALID_INPUT, error),
          );
          if (!scrollInViewIssued) {
            this.algoPanelControlSource.next(
              new OECommandEventModel(refId, OENCommand.SCROLL_IN_VIEW, error),
            );
            scrollInViewIssued = true;
          }
        } else {
          this.entryPanelControlSource.next(
            new OECommandEventModel(refId, OENCommand.INVALID_INPUT, error),
          );
        }
      }
      // activate tab
      let financialsErrors = errors.filter((item) => {
        return item.panel === OENPanel.PANEL_FINANCIALS;
      });
      let moreErrors = errors.filter((item) => {
        return item.panel === OENPanel.PANEL_MORE;
      });
      if (financialsErrors != null && financialsErrors.length > 0) {
        this.orderEntryScreenControlSource.next(
          new OECommandEventModel(refId, OENCommand.SHOW_TAB_FINANCIALS),
        );
      } else if (moreErrors != null && moreErrors.length > 0) {
        this.orderEntryScreenControlSource.next(
          new OECommandEventModel(refId, OENCommand.SHOW_TAB_MORE),
        );
      }
    }
  }

  public cmdChangeDisplayState(refId: string, command: OENCommand, data?: any): void {
    if (command != null && OE_DISPLAY_STATE_COMMANDS.has(command)) {
      this.setter.setOrderDisplayState(refId, this.utility.transformCmdToDisplayState(command));

      if (!this.getter.has(refId)) {
        logger.out.warn(`oecord-s - Cannot find ref Id ${refId} in master`);
        return;
      }
      const rootRefId = this.getter.getRootIdForNodeWithRefId(refId) ?? refId;
      this.actionsPanelControlSource.next(new OECommandEventModel(rootRefId, command, data));
      this.notificationsPanelControlSource.next(new OECommandEventModel(rootRefId, command, data));
      this.orderEntryScreenControlSource.next(new OECommandEventModel(refId, command, data));
      this.debugPanelControlSource.next(new OECommandEventModel(refId, command, data));

      switch (command) {
        case OENCommand.STATE_ORDER_ENTRY:
        case OENCommand.STATE_ORDER_IN_MODIFICATION:
          // unlock all for root and all strategy legs
          let nodesToUnlock: Array<string> = this.getter.getStrategyNodesListRoot(refId);
          nodesToUnlock.forEach((nodeRefId: string) => {
            this.algoPanelControlSource.next(
              new OECommandEventModel(nodeRefId, OENCommand.UNLOCK_ALL),
            );
            this.entryPanelControlSource.next(
              new OECommandEventModel(nodeRefId, OENCommand.UNLOCK_ALL),
            );
            this.notificationsPanelControlSource.next(
              new OECommandEventModel(nodeRefId, OENCommand.UNLOCK_ALL),
            );
          });
          break;
        case OENCommand.STATE_MONITOR_EXECUTION:
        case OENCommand.STATE_ORDER_PROCESSING:
        case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_ERROR:
        case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_WARN:
        case OENCommand.BM_ZERO_AMOUNT_WARN:
        case OENCommand.STATE_ORDER_ENTRY_NEGATIVE_MARKUP:
          // lock everything
          let nodesToLock: Array<string> = this.getter.getStrategyNodesListRoot(refId);
          nodesToLock.forEach((nodeRefId: string) => {
            this.algoPanelControlSource.next(
              new OECommandEventModel(nodeRefId, OENCommand.LOCK_ALL),
            );
            this.entryPanelControlSource.next(
              new OECommandEventModel(nodeRefId, OENCommand.LOCK_ALL),
            );
            this.notificationsPanelControlSource.next(
              new OECommandEventModel(nodeRefId, OENCommand.LOCK_ALL),
            );
          });
          break;
        default:
          break;
      }
    } else if (command == OENCommand.STATE_REVAL_BUTTONS) {
      this.actionsPanelControlSource.next(new OECommandEventModel(refId, command, data));
    }
  }

  /**
   * Freezes all controlls in the entry dialog. Wonder what's the difference between
   * freeze and lock? Freeze will disable actions at the top of the popup, including
   * close and settings as well.
   *
   * @param refId the reference number of the order created or edited in this dialog
   */
  public cmdFreezeInterfaceControls(refId: string): void {
    this.cmdLockInterfaceControls(refId);
    this.orderEntryScreenControlSource.next(new OECommandEventModel(refId, OENCommand.FREEZE));
  }

  /**
   * Reverse from freeze, see comment above
   *
   * @param refId the reference number of the order created or edited in this dialog
   */
  public cmdUnfreezeInterfaceControls(refId: string): void {
    this.orderEntryScreenControlSource.next(new OECommandEventModel(refId, OENCommand.UNFREEZE));
    this.cmdUnlockInterfaceControls(refId);
  }

  /**********************************************************************/
  /* LOCKING interface controls */

  /* CMD - Lock/Unlock controls */
  public cmdLockInterfaceControls(refId: string, lockActions: boolean = true): void {
    this.entryPanelControlSource.next(new OECommandEventModel(refId, OENCommand.LOCK_ALL));
    this.algoPanelControlSource.next(new OECommandEventModel(refId, OENCommand.LOCK_ALL));
    this.notificationsPanelControlSource.next(new OECommandEventModel(refId, OENCommand.LOCK_ALL));
    this.debugPanelControlSource.next(new OECommandEventModel(refId, OENCommand.LOCK_ALL));
    if (lockActions)
      this.actionsPanelControlSource.next(new OECommandEventModel(refId, OENCommand.LOCK_ALL));
  }

  public cmdUnlockInterfaceControls(refId: string): void {
    this.entryPanelControlSource.next(new OECommandEventModel(refId, OENCommand.UNLOCK_ALL));
    this.algoPanelControlSource.next(new OECommandEventModel(refId, OENCommand.UNLOCK_ALL));
    this.actionsPanelControlSource.next(new OECommandEventModel(refId, OENCommand.UNLOCK_ALL));
    this.notificationsPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.UNLOCK_ALL),
    );
    this.debugPanelControlSource.next(new OECommandEventModel(refId, OENCommand.UNLOCK_ALL));
  }

  /**
   * If we've lost visibility of any order ina strategy, then we must disable all orders in the strategy, even those
   * whose trader groups we appear to be able to see (e.g. filled legs)
   * @param refId
   */
  public cmdHideLostOrder(refId: string) {
    const rootRefId = this.getter.getRootRefIdForStrategyRefId(refId);
    if (rootRefId) {
      this.cmdLockInterfaceControls(refId, false);
      this.cmdChangeDisplayState(rootRefId, OENCommand.STATE_ORDER_LOST_HIDDEN);
    }
  }

  public cmdLoadInterfaceControlLockingProfile(
    refId: string,
    lockProfile: OELockingProfileModel,
  ): void {
    // ENTRY FORM
    let entryFormLockingProfile: OELockingProfileTransportModel =
      new OELockingProfileTransportModel();
    entryFormLockingProfile.profile = lockProfile.getLockProfile();
    entryFormLockingProfile.profileType = lockProfile.getLockingProfileType();
    if (this.utility.checkLockingProfileTransportModel(entryFormLockingProfile)) {
      this.entryPanelControlSource.next(
        new OECommandEventModel(refId, OENCommand.LOAD_LOCKING_PROFILE, entryFormLockingProfile),
      );
    }
    // NOTIFICATIONS
    let notificationsLockingProfile: OELockingProfileTransportModel =
      new OELockingProfileTransportModel();
    notificationsLockingProfile.profile = lockProfile.getLockProfileForPanel(
      OENPanel.PANEL_NOTIFICATIONS,
    );
    notificationsLockingProfile.profileType = lockProfile.getLockingProfileType();
    if (this.utility.checkLockingProfileTransportModel(notificationsLockingProfile)) {
      this.notificationsPanelControlSource.next(
        new OECommandEventModel(
          refId,
          OENCommand.LOAD_LOCKING_PROFILE,
          notificationsLockingProfile,
        ),
      );
    }
    // ALGO PANEL
    let algoParamsLockingProfile: OELockingProfileTransportModel =
      new OELockingProfileTransportModel();
    algoParamsLockingProfile.profile = lockProfile.getLockProfileForPanel(OENPanel.PANEL_ALGO);
    algoParamsLockingProfile.profileType = lockProfile.getLockingProfileType();
    if (this.utility.checkLockingProfileTransportModel(algoParamsLockingProfile)) {
      this.algoPanelControlSource.next(
        new OECommandEventModel(refId, OENCommand.LOAD_LOCKING_PROFILE, algoParamsLockingProfile),
      );
    }
  }

  // NOTIFICATIONS PANEL - STRATEGY BUTTONS
  public cmdUpdateStrategyButtonsLockingWithMKTCheck(
    refId: string,
    cancelIfNotMKT: boolean = false,
  ): void {
    if (this.getter.has(refId)) {
      if (
        (this.getter.isEntryTypeSpot(refId) || this.getter.isEntryTypeNdf(refId)) &&
        this.getter.isOrderTypeMKT(refId)
      ) {
        let lockProfile: OELockingProfileModel = new OELockingProfileModel();
        lockProfile.setLockingProfileType(OENLockingProfileType.TEMPORARY);
        let strategyButtons = [
          OENInputControl.BTN_IF_DONE,
          OENInputControl.BTN_LOOP,
          OENInputControl.BTN_OCO,
          OENInputControl.BTN_TIMEOUT,
        ];
        for (let btnId of strategyButtons) {
          lockProfile.updateInputLockState(
            new OEInputLockStateItemModel(
              INPUT_TO_PANEL_MAPPING.get(btnId),
              btnId,
              MKT_ORDER_LOCK_MAPPING.get(btnId),
            ),
          );
        }
        this.cmdUpdateStrategyButtonsLocking(refId, lockProfile);
      } else if (!cancelIfNotMKT) {
        this.cmdUpdateStrategyButtonsLocking(refId);
      }
    }
  }

  public cmdUpdateStrategyButtonsLocking(
    refId: string,
    lockProfile: OELockingProfileModel = null,
  ): void {
    let notificationsLockProfile: OELockingProfileTransportModel =
      new OELockingProfileTransportModel();
    if (lockProfile == null && this.getter.has(refId)) {
      // && this.getter.hasStrategy(refId)
      notificationsLockProfile.profile = this.getter
        .get(refId)
        .strategy.lockProfile.getLockProfileForPanel(OENPanel.PANEL_NOTIFICATIONS);
      notificationsLockProfile.profileType = this.getter
        .get(refId)
        .strategy.lockProfile.getLockingProfileType();
    } else {
      notificationsLockProfile.profile = lockProfile.getLockProfileForPanel(
        OENPanel.PANEL_NOTIFICATIONS,
      );
      notificationsLockProfile.profileType = lockProfile.getLockingProfileType();
    }
    // transmit login events
    if (this.utility.checkLockingProfileTransportModel(notificationsLockProfile)) {
      this.notificationsPanelControlSource.next(
        new OECommandEventModel(refId, OENCommand.LOAD_LOCKING_PROFILE, notificationsLockProfile),
      );
    }
  }

  // ACTIONS PANEL - SAVE BUTTON
  // used for disabling/enabling SAVE button in Actions Panel for MKT Order
  public cmdUpdateActionsSaveBtnAvailability(refId: string, saveDisabled: boolean): void {
    this.actionsPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.UPDATE_SAVE_BTN_AVAILABILITY, saveDisabled),
    );
  }

  // ACTIONS PANEL - SAVE BUTTON
  // used for disabling/enabling SAVE button in Actions Panel for MKT Order
  public cmdUpdateGammaIneligibilty(refId: string): void {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.UPDATE_GAMMA_INELIGIBLE),
    );
  }

  // ACTIONS PANEL - SAVE BUTTON
  // used for disabling/enabling SAVE button in Actions Panel for MKT Order
  public cmdUpdateGammaEligibilty(refId: string): void {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.UPDATE_GAMMA_ELIGIBLE),
    );
  }

  //enables customise buttton for gammas if order form is ready and popped
  public enabledGammaCustomiseButton(refId: string): void {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.GAMMA_ENABLE_CUST_BUTTON),
    );
  }

  //dsiables customise buttton for gammas if order form is not ready and popped
  public disableGammaCustomiseButton(refId: string): void {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.GAMMA_DISABLE_CUST_BUTTON),
    );
  }

  public cmdLoadGammaFromDuplicate(refId: string): void {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.GAMMA_DUPLICATE_LOAD),
    );
  }

  /*
  Negative response from gamma viewer to custom component. leg rejected.
   */
  public sendNewLegResponse(refId: string) {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.GAMMA_NEW_LEG_RESP),
    );
  }

  /**
   * A leg custom rate has changed -
   * @param refId
   */
  public sendLegCustomRateChange(refId: string) {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.GAMMA_CUSTOM_RATE_CHANGE),
    );
  }

  public sendLockingGammaProfile(refId: string) {
    let profile: OELockingProfileTransportModel = new OELockingProfileTransportModel();
    profile.profileType = OENLockingProfileType.TEMPORARY;
    profile.profile = [
      new OEInputLockStateItemModel(OENPanel.PANEL_FINANCIALS, null, OENLockedState.LOCKED),
    ];

    this.entryPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.LOAD_LOCKING_PROFILE, profile),
    );
  }

  /*Synch the gamma viewer model with the model.
   */
  public saveGammaModel(rootRefId: string) {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(rootRefId, OENCommand.SAVE_GAMMA_TO_MODEL),
    );
  }

  /**********************************************************************/
  /* RESET interface controls */
  public cmdResetAllInterfaceControls(refId: string): void {
    this.entryPanelControlSource.next(new OECommandEventModel(refId, OENCommand.RESET_ALL));
    this.notificationsPanelControlSource.next(new OECommandEventModel(refId, OENCommand.RESET_ALL));
    this.strategyPanelControlSource.next(new OECommandEventModel(refId, OENCommand.RESET_ALL));
    this.orderEntryScreenControlSource.next(new OECommandEventModel(refId, OENCommand.RESET_ALL));

    //this.algoPanelControlSource.next(new OECommandEventModel(refId, OENCommand.RESET_ALL));
  }

  /**********************************************************************/
  /* LOAD  Interface controls */
  public cmdLoadInterfaceControlData(refId: string, data: OEDLoadingDataModel): void {
    this.entryPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.LOAD_CONTROLS, data),
    );
    if (data && data.algoValues instanceof Array && data.algoValues.length > 0) {
      this.algoPanelControlSource.next(
        new OECommandEventModel(refId, OENCommand.LOAD_CONTROLS, data),
      );
    }

    // Find the intent load value and propagate though to all
    const intentModel = data.values.find(
      (valueModel) => valueModel.input == OENInputControl.CLIENT_INTENT,
    );
    const intentValue = intentModel?.value ?? null;
    logger.out.info(`oecord-s - propaging intent of intentValue for orderRef ${refId}`);
    this.cmdPropagateClientIntentChange(refId, intentValue);
  }

  /**********************************************************************/
  /* CLOSE Screen */
  public cmdCloseOrderEntryScreen(refId: string, autoClose: boolean = null): void {
    this.orderEntryScreenControlSource.next(
      new OECommandEventModel(refId, OENCommand.LOGOUT_CLEANUP_ORDER_ENTRY, autoClose),
    );
  }

  /**
   * Change the Entry window to execution-monitoring mode, ensuring nothing is editable
   * @param refId
   */
  public cmdMonitorExecution(refId: string): void {
    logger.out.info("oecord-s - switching to monitoring execution mode");

    this.setter.setOperationModeToMonitorExecution(refId);
    this.cmdChangeDisplayState(refId, OENCommand.STATE_MONITOR_EXECUTION);
    const lockAllCmd = new OECommandEventModel(refId, OENCommand.LOCK_ALL);
    this.entryPanelControlSource.next(lockAllCmd);
    this.algoPanelControlSource.next(lockAllCmd);
  }

  /**
   * Change the Entry window to modify when monitoring an order - will enable title updates etc
   * @param refId
   */
  public cmdModifyMonitoredOrder(refId: string, mode: OENOperationMode): void {
    const currentDisplayState = this.getter.getDisplayState(refId);
    if (currentDisplayState !== OENDisplayState.SHOW_LOST_HIDDEN) {
      this.orderEntryScreenControlSource.next(
        new OECommandEventModel(refId, OENCommand.MODIFY_MONITORED_ORDER, mode),
      );
    }
  }

  /**
   * Notify interested parties that async post loading of control data is complete - at this stage we can depend
   * on the values in the models being what they're supposed to be (either init or defaults)
   * @param refId
   */
  public cmdLoadingComplete(refId: string): void {
    this.algoPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.CONTROL_LOADING_COMPLETE),
    );
  }

  /**
   * Notify interested parties to process a new callout notification from an Execution Monitor
   * @param refId
   */
  public cmdExecutionNotification(refId: string, data: IActionCallout): void {
    this.notificationsPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.EXECUTION_NOTIFICATION, data),
    );
  }

  public cmdFocusOrderEntryScreen(refId: string): void {
    this.orderEntryScreenControlSource.next(
      new OECommandEventModel(refId, OENCommand.FOCUS_ORDER_ENTRY),
    );
  }

  /**********************************************************************/
  /* MANAGE REFIDs -   Management of multiple refIds (used for strategy activation)*/
  public cmdAddStrategyLegRefListener(refId: string, strategyLegRefId: string): void {
    this.orderEntryScreenControlSource.next(
      new OECommandEventModel(refId, OENCommand.REF_ADD, strategyLegRefId),
    );
  }

  public cmdRemoveStrategyLegRefListener(
    refId: string,
    strategyLegRefId: string,
    removeRoot: boolean = false,
  ): void {
    if (removeRoot) {
      this.orderEntryScreenControlSource.next(
        new OECommandEventModel(refId, OENCommand.REF_REMOVE_ROOT, strategyLegRefId),
      );
    } else {
      this.orderEntryScreenControlSource.next(
        new OECommandEventModel(refId, OENCommand.REF_REMOVE, strategyLegRefId),
      );
    }
  }

  public cmdActivateStrategyLegRefListener(refId: string): void {
    this.setter.setActivelyDisplayed(refId);
    this.orderEntryScreenControlSource.next(
      new OECommandEventModel(refId, OENCommand.REF_ACTIVATE),
    );
  }

  /**********************************************************************/
  /* STRATEGY LAYOUT */
  public cmdDisplayStrategyLayoutChange(refId: string, command: OENCommand, data?: any): void {
    if (command != null) {
      switch (command) {
        case OENCommand.PANEL_HIDE_STRATEGY:
          this.orderEntryScreenControlSource.next(new OECommandEventModel(refId, command));
          break;
        case OENCommand.PANEL_UPDATE_STRATEGY:
          // delay added for sync with  OENCommand.PANEL_SHOW_STRATEGY_LAYOUT, must be after
          setTimeout(() => {
            this.strategyPanelControlSource.next(new OECommandEventModel(refId, command));
          }, 100);
          break;
        case OENCommand.PANEL_SHOW_STRATEGY:
          this.cmdRenderStrategyLayout(refId, data);
          break;
        default:
          logger.out.error(
            `ERROR: _oeCoordinator.cmdDisplayStrategyLayoutChange(): Invalid command= ${command}`,
          );
          break;
      }
    }
  }

  public cmdRefreshStrategyLayout(refId: string, data: any = null) {
    if (!this.getter.has(refId)) {
      logger.out.warn(`Cannot refresh strategy - refId ${refId} not found`);
      return;
    }

    const singleOrder = this.getter.isRootNodeAlone(refId);
    if (singleOrder) {
      this.orderEntryScreenControlSource.next(
        new OECommandEventModel(refId, OENCommand.PANEL_HIDE_STRATEGY),
      );
    } else {
      if (data == null) {
        data = this.strategyLayout.getStrategyLayout(refId);
      }
      setTimeout(() => {
        this.strategyPanelControlSource.next(
          new OECommandEventModel(refId, OENCommand.PANEL_RESET_STRATEGY),
        );
      }, 100);
      setTimeout(() => {
        this.strategyPanelControlSource.next(
          new OECommandEventModel(refId, OENCommand.PANEL_SHOW_STRATEGY, data),
        );
      }, 300);
    }
  }

  public cmdUpdateStrategyLayout(refId: string, updateAllNodes: boolean = false): void {
    if (updateAllNodes) {
      let strategyLegs: Array<string> = this.getter.getStrategyNodesListRoot(refId);
      if (strategyLegs != null && strategyLegs.length > 0) {
        strategyLegs.forEach((itemRefId: string) => {
          this.strategyPanelControlSource.next(
            new OECommandEventModel(itemRefId, OENCommand.PANEL_UPDATE_STRATEGY),
          );
        });
      }
    } else {
      this.strategyPanelControlSource.next(
        new OECommandEventModel(refId, OENCommand.PANEL_UPDATE_STRATEGY),
      );
    }
  }

  public cmdEnableCustomiseGammas(refId: string): void {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.GAMMA_CUSTOMISE),
    );
  }
  public cmdDisableCustomiseGammas(refId: string): void {
    this.strategyPanelControlSource.next(new OECommandEventModel(refId, OENCommand.GAMMA_RESTORE));
  }

  /** used after inplace 'Copy' action */
  public cmdUnbulk(refId: string, source: OENInputControl): void {
    this.entryPanelControlSource.next(new OECommandEventModel(refId, OENCommand.UNBULK, source));
    this.actionsPanelControlSource.next(new OECommandEventModel(refId, OENCommand.UNBULK, source));
  }

  public cmdBulked(refId: string, source: OENInputControl): void {
    this.entryPanelControlSource.next(new OECommandEventModel(refId, OENCommand.BULKED, source));
    this.actionsPanelControlSource.next(new OECommandEventModel(refId, OENCommand.BULKED, source));
  }

  public cmdBulking(refId: string, source: OENInputControl): void {
    this.entryPanelControlSource.next(new OECommandEventModel(refId, OENCommand.BULKING, source));
    this.actionsPanelControlSource.next(new OECommandEventModel(refId, OENCommand.BULKING, source));
  }

  public sendTopGammaLeg(refId: string, data: any): void {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.GAMMA_TOP_LEG, data),
    );
  }

  public sendCustomLegToViewer(refId: string, gammaTopLeg: OEPanelGammaLeg) {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.GAMMA_UPDATED_CUST_LEG, gammaTopLeg),
    );
  }

  public sendCcyPairToViewer(refId: string, currencyPair: any) {
    this.strategyPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.GAMMA_CUST_CCYPAIR_UPDATE, currencyPair),
    );
  }

  public cmdRenderStrategyLayout(refId: string, data?: any): void {
    if (data == null) {
      data = this.strategyLayout.getStrategyLayout(refId);
    }
    this.orderEntryScreenControlSource.next(
      new OECommandEventModel(refId, OENCommand.PANEL_SHOW_STRATEGY),
    );
    // added timeout so that the panel is shown by the screen before strategy rendering starts
    setTimeout(() => {
      this.strategyPanelControlSource.next(
        new OECommandEventModel(refId, OENCommand.PANEL_SHOW_STRATEGY, data),
      );
    }, 50);
  }

  public cmdPropagateClientIntentChange(refId: string, _clientIntent: CubiqTS.OrderIntent): void {
    this.actionsPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.PROPAGATE_CLIENT_INTENT_CHANGE, _clientIntent),
    );
  }

  /**********************************************************************/
  /* DEBUG - Logging */
  public cmdUpdateLoggingStateDisplay(
    loggingType: OENExtendedLoggingType,
    extendedLoggingState: boolean,
    modelDumpLoggingState: boolean,
  ): void {
    let screensToNotify = this.getter.getAllRootRefs();
    if (screensToNotify instanceof Array && screensToNotify.length > 0) {
      screensToNotify.forEach((rootRefId: string) => {
        this.debugPanelControlSource.next(
          new OECommandEventModel(rootRefId, OENCommand.EXTENDED_LOG_UPDATE, {
            type: loggingType,
            extLog: extendedLoggingState,
            mdLog: modelDumpLoggingState,
          }),
        );
      });
    }
  }

  /**********************************************************************/
  /* DEBUG - Dev TEST - Algos */

  public testValidateAlgoPanel(refId: string): void {
    this.algoPanelControlSource.next(new OECommandEventModel(refId, OENCommand.VALIDATE));
  }

  public testAlgoControlValidationState(refId: string, value: boolean): void {
    this.algoPanelControlSource.next(
      new OECommandEventModel(refId, OENCommand.ATDL_CONTROL_VALIDATION, value),
    );
  }
}
